import { CommonModule } from '@angular/common';
import { Component, Input, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { FormGroup, ReactiveFormsModule } from '@angular/forms';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { NgIconComponent } from '@ng-icons/core';
import { ToastService } from '../../../../../main/services';
import { TenderAwardsService } from '../../../../services/tender-awards.service';

@Component({
    selector: 'jft-email-preview-modal',
    templateUrl: './email-preview-modal.component.html',
    styleUrls: ['./email-preview-modal.component.scss'],
    standalone: true,
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [CommonModule, ReactiveFormsModule, NgIconComponent]
})
export class EmailPreviewModalComponent implements OnInit {
    @Input() tender: any;
    @Input() supplierGroup: any;
    @Input() awardedOffers: any[] = [];

    public form: FormGroup;
    public loading: boolean = false;
    public previewLoading: boolean = true;
    public previewError: string = '';
    public emailPreview: string = '';
    public safeEmailPreview: SafeHtml | null = null;
    public emailSubject: string = '';

    constructor(
        public activeModal: NgbActiveModal,
        private awardsService: TenderAwardsService,
        private toast: ToastService,
        private sanitizer: DomSanitizer
    ) {
        this.form = new FormGroup({});
    }

    ngOnInit() {
        this.loadEmailPreview();
    }

    private loadEmailPreview() {
        const supplierId = this.supplierGroup?.supplier?.id;
        const offerIds = this.awardedOffers
            .filter(offer => offer.status === 'AWARDED')
            .map(offer => offer.id);

        if (!supplierId || !offerIds.length || !this.tender?.id) {
            this.previewLoading = false;
            this.previewError = 'Unable to load preview.';
            return;
        }

        this.previewLoading = true;
        this.previewError = '';
        this.awardsService
            .previewAwardNotification(this.tender.id, supplierId, offerIds)
            .subscribe({
                next: (response) => {
                    this.emailPreview = response.html;
                    // Trusted only as a sandboxed iframe srcdoc (see the template), never as page HTML.
                    this.safeEmailPreview = this.sanitizer.bypassSecurityTrustHtml(response.html ?? '');
                    this.emailSubject = response.subject;
                    this.previewLoading = false;
                },
                error: () => {
                    this.previewLoading = false;
                    this.previewError = 'Failed to load preview.';
                },
            });
    }

    public getEmailSubject(): string {
        if (this.emailSubject) {
            return this.emailSubject;
        }
        const tenderName = this.tender?.name || 'Tender';
        return `Award Notification - ${tenderName}`;
    }


    public sendEmail() {
        this.loading = true;

        const emailData = {
            supplier_id: this.supplierGroup.supplier.id,
            offer_ids: this.awardedOffers.map(offer => offer.id),
            custom_note: ''
        };

        // Send actual email via API
        this.awardsService.sendAwardNotification(this.tender.id, emailData).subscribe({
            next: (response) => {
                this.loading = false;
                this.toast.success(`Award notification sent to ${this.supplierGroup.supplier.name}`);
                this.activeModal.close({
                    sent: true,
                    supplierGroup: this.supplierGroup,
                    emailData: emailData,
                    response: response
                });
            },
            error: (error) => {
                this.loading = false;
                console.error('Failed to send award notification:', error);
                this.toast.error(`Failed to send notification to ${this.supplierGroup.supplier.name}`);
                // Don't close modal on error so user can retry
            }
        });
    }

    public getTentativeOffersCount(): number {
        return this.awardedOffers.filter(offer =>
            offer.status === 'TENTATIVE_AWARDED' || offer.status === 'TENTATIVELY_AWARDED'
        ).length;
    }

    public getFinalOffersCount(): number {
        return this.awardedOffers.filter(offer => offer.status === 'AWARDED').length;
    }

    public hasMixedAwards(): boolean {
        return this.getTentativeOffersCount() > 0 && this.getFinalOffersCount() > 0;
    }

    public cancel() {
        this.activeModal.dismiss('cancel');
    }
}
